<template>
  <div class="new-customer-modal flex flex-col w-full h-full">
    <!-- Primary shortcut: import many customers at once from the phone's contacts -->
    <button
      v-if="showImport"
      type="button"
      class="w-full flex items-center gap-3 p-3 mb-4 rounded-xl border border-primary-200 bg-primary-50/60 active:bg-primary-100 transition-colors"
      @click="$emit('import')"
    >
      <span class="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-none">
        <i class="pi pi-address-book text-primary-600" />
      </span>
      <span class="flex-1 min-w-0 text-right">
        <span class="block text-sm font-semibold text-gray-900">ייבוא מאנשי הקשר</span>
        <span class="block text-xs text-gray-500">הוסיפו הרבה לקוחות בבת אחת מהטלפון</span>
      </span>
      <i class="pi pi-chevron-left text-gray-400 text-xs flex-none" />
    </button>

    <div v-if="showImport" class="flex items-center gap-3 mb-4">
      <span class="flex-1 h-px bg-gray-200" />
      <span class="text-xs text-gray-400">או הוסיפו ידנית</span>
      <span class="flex-1 h-px bg-gray-200" />
    </div>

    <div class="new-event">
      <span class="subtitle-bold section mb-1">שם מלא</span>
      <input-text v-model="newCustomer.fullName" placeholder="שם מלא" type="text" />
      <small v-show="fullNameError" class="error">{{ fullNameError }}</small>

      <span class="subtitle-bold section mt-2 mb-1">מספר סלולרי</span>
      <InputMask
        v-model="newCustomer.phoneNumber"
        placeholder="מה מספר הפלאפון של הלקוח?"
        inputmode="numeric"
        mask="999-999-9999"
        pattern="[0-9]*"
      />
      <small v-show="phoneNumberError" class="error">{{ phoneNumberError }}</small>

    </div>

    <div class="mt-auto">
      <drawer-footer
        :approveDisable="!validateFilled"
        :approveLoading="loading"
        @approve-click="clickCTA"
        @cancel-click="$emit('close')"
      />
    </div>
  </div>

</template>

<script lang="ts" setup>
import type { ICustomer } from '@torim-app/types';

import { computed, reactive, ref } from 'vue';
import { useToast } from 'vue-toastification';

import { cleanPhoneNumber, validateInputPhone } from '@torim-app/utils';
import { CustomersApi } from '@app/api/admin/customers-api';
import { useAdminClientsStore } from '@app/stores';
import { trackEvent } from '@app/composables/posthog';

defineProps<{ showImport?: boolean }>();
const emit = defineEmits(['close', 'import']);

const adminClientsStore = useAdminClientsStore();
const toast = useToast();

const newCustomer = <Partial<ICustomer>>reactive({});
const loading = ref(false);
const phoneNumberError = ref('');
const fullNameError = ref('');

const validateFilled = computed<boolean>(() => {
  return !!newCustomer.fullName && !!newCustomer.phoneNumber;
});

const validateFields = (): boolean => {
  let hasError = false;

  phoneNumberError.value = '';
  fullNameError.value = '';

  if (newCustomer.phoneNumber && !validateInputPhone(newCustomer.phoneNumber)) {
    phoneNumberError.value = 'יש להזין מספר פלאפון תקין';
    hasError = true;
  }

  if (!newCustomer.fullName || newCustomer.fullName.length < 2 || newCustomer.fullName.trim() === '') {
    hasError = true;
    fullNameError.value = 'יש להזין שם מלא';
  }

  return hasError;
};

const clickCTA = async (): Promise<void> => {
  trackEvent('customer_created');
  if (validateFilled.value && !validateFields()) {
    loading.value = true;
    try {
      await CustomersApi.addCustomer([{
        fullName: newCustomer.fullName,
        phoneNumber: cleanPhoneNumber(<string>newCustomer.phoneNumber)
      }]);
      adminClientsStore.resetCustomersList();
      await adminClientsStore.fetchBusinessCustomers();
      toast.success('הלקוח נשמר במערכת!');
      emit('close');
    } catch (e) {
      console.error('failed to load customer-panel' + e);
      if (e === 'customer-panel already exists')
        toast.error('לקוח עם מספר הטלפון הזה כבר קיים');
      else
        toast.error('לא הצלחנו לשמור את הלקוח');
    }
    loading.value = false;
  }
};
</script>

<style lang="scss" scoped>

.new-customer-modal {
  width: 100%;

  .new-event {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    flex-direction: column;

    .p-inputtext {
      width: 100%;
      margin-bottom: 0.5rem;
    }

    .subtitle-bold {
      margin-bottom: 0.3rem;
    }
  }

  .error {
    color: red;
    margin-bottom: 0.75rem;
  }
}

</style>
